/**
 * Hook para tracking de impressões de componentes SDUI
 *
 * Implementa:
 * - Detecção de visibilidade com IntersectionObserver
 * - Threshold configurável (% visível + tempo mínimo)
 * - Deduplicação por sessão para evitar spam
 */

import { useEffect, useRef, useCallback } from 'react';
import { analyticsService } from './analytics';

/**
 * Opções para tracking de impressão
 */
interface ImpressionOptions {
  /** Tipo do componente (ex: 'venue_card', 'hero_banner') */
  componentType: string;
  /** Chave estável do componente (ex: 'venue_123', 'hero_main') */
  componentKey: string;
  /** Posição do componente (opcional) */
  position?: number;
  /** Threshold de visibilidade (0.0 a 1.0, padrão: 0.5 = 50%) */
  threshold?: number;
  /** Tempo mínimo visível em ms (padrão: 500ms) */
  minVisibleTime?: number;
  /** Desabilitar tracking (útil para conditional rendering) */
  disabled?: boolean;
}

/**
 * Cache de impressões por sessão
 * Evita enviar impressão duplicada do mesmo componente na mesma sessão
 */
const impressionCache = new Set<string>();

/**
 * Gera chave única para o componente
 */
function getImpressionKey(componentType: string, componentKey: string): string {
  return `${componentType}:${componentKey}`;
}

/**
 * Verifica se já enviou impressão para este componente nesta sessão
 */
function hasTrackedImpression(componentType: string, componentKey: string): boolean {
  const key = getImpressionKey(componentType, componentKey);
  return impressionCache.has(key);
}

/**
 * Marca componente como impresso
 */
function markAsTracked(componentType: string, componentKey: string): void {
  const key = getImpressionKey(componentType, componentKey);
  impressionCache.add(key);
}

/**
 * Hook React para tracking de impressões de componentes SDUI
 *
 * Uso:
 * ```tsx
 * function VenueCard({ venue, position }) {
 *   const ref = useComponentImpression({
 *     componentType: 'venue_card',
 *     componentKey: `venue_${venue.id}`,
 *     position
 *   });
 *
 *   return <div ref={ref}>...</div>;
 * }
 * ```
 *
 * @param options - Opções de tracking
 * @returns Callback ref para anexar ao elemento DOM
 */
export function useComponentImpression(options: ImpressionOptions) {
  const {
    componentType,
    componentKey,
    position,
    threshold = 0.5,
    minVisibleTime = 500,
    disabled = false
  } = options;

  const elementRef = useRef<HTMLElement | null>(null);
  const visibilityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Callback ref que será retornado
  const callbackRef = useCallback((node: HTMLElement | null) => {
    // Cleanup do elemento anterior
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (visibilityTimerRef.current) {
      clearTimeout(visibilityTimerRef.current);
      visibilityTimerRef.current = null;
    }

    // Guarda referência ao elemento
    elementRef.current = node;

    // Se desabilitado, já tracked, ou sem elemento, não observar
    if (disabled || !node || hasTrackedImpression(componentType, componentKey)) {
      return;
    }

    if (!analyticsService.isInitialized()) {
      return;
    }

    // Cria observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Elemento está visível acima do threshold
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            // Inicia timer para verificar tempo mínimo visível
            if (!visibilityTimerRef.current) {
              visibilityTimerRef.current = setTimeout(() => {
                // Ainda não tracked?
                if (!hasTrackedImpression(componentType, componentKey)) {
                  // Envia evento de impressão
                  analyticsService.track('component_impression', {
                    component_type: componentType,
                    component_key: componentKey,
                    ...(position !== undefined && { position })
                  });

                  // Marca como tracked
                  markAsTracked(componentType, componentKey);
                }

                visibilityTimerRef.current = null;
              }, minVisibleTime);
            }
          } else {
            // Elemento ficou invisível antes do tempo mínimo
            if (visibilityTimerRef.current) {
              clearTimeout(visibilityTimerRef.current);
              visibilityTimerRef.current = null;
            }
          }
        });
      },
      {
        threshold,
        // Margem adicional para começar a observar antes do elemento entrar na viewport
        rootMargin: '50px'
      }
    );

    // Começa a observar
    observerRef.current.observe(node);
  }, [componentType, componentKey, position, threshold, minVisibleTime, disabled]);

  // Cleanup quando desmonta ou dependências mudam
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
      }
    };
  }, []);

  return callbackRef;
}

/**
 * Limpa o cache de impressões (útil para testes ou reset de sessão)
 */
export function clearImpressionCache(): void {
  impressionCache.clear();
}
